import { Injectable, effect, signal, computed, PLATFORM_ID, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

export interface LayoutConfig {
  preset?: string;
  primary?: string;
  surface?: string | null;
  darkTheme?: boolean;
  menuMode?: string;
}

interface LayoutState {
  staticMenuDesktopInactive?: boolean;
  overlayMenuActive?: boolean;
  configSidebarVisible?: boolean;
  staticMenuMobileActive?: boolean;
  menuHoverActive?: boolean;
}

interface MenuChangeEvent {
  key: string;
  routeEvent?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private readonly LAYOUT_CONFIG_KEY = 'layout-config';
  private readonly platformId = inject(PLATFORM_ID);

  _config: LayoutConfig = {
    preset: 'Aura',
    primary: 'rose',
    surface: null,
    darkTheme: false,
    menuMode: 'static',
  };

  _state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
  };

  readonly layoutConfig = signal<LayoutConfig>(this._config);

  readonly layoutState = signal<LayoutState>(this._state);

  private readonly configUpdate = new Subject<LayoutConfig>();

  private readonly overlayOpen = new Subject<void>();

  private readonly menuSource = new Subject<MenuChangeEvent>();

  private readonly resetSource = new Subject<boolean>();

  readonly menuSource$ = this.menuSource.asObservable();

  readonly resetSource$ = this.resetSource.asObservable();

  readonly configUpdate$ = this.configUpdate.asObservable();

  readonly overlayOpen$ = this.overlayOpen.asObservable();

  readonly theme = computed(() => (this.layoutConfig()?.darkTheme ? 'light' : 'dark'));

  readonly isSidebarActive = computed(
    () => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive
  );

  readonly isDarkTheme = computed(() => this.layoutConfig().darkTheme);

  readonly getPrimary = computed(() => this.layoutConfig().primary);

  readonly getSurface = computed(() => this.layoutConfig().surface);

  readonly isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

  readonly transitionComplete = signal<boolean>(false);

  private initialized = false;

  constructor() {
    // Charger la configuration sauvegardée ou utiliser les valeurs par défaut
    if (isPlatformBrowser(this.platformId)) {
      this.loadConfig();
    }

    effect(() => {
      const config = this.layoutConfig();
      if (config) {
        this.onConfigUpdate();
      }
    });

    effect(() => {
      const config = this.layoutConfig();

      if (!this.initialized || !config) {
        this.initialized = true;
        return;
      }

      this.handleDarkModeTransition(config);
    });
  }

  private handleDarkModeTransition(config: LayoutConfig): void {
    if ((document as any).startViewTransition) {
      this.startViewTransition(config);
    } else {
      this.toggleDarkMode(config);
      this.onTransitionEnd();
    }
  }

  private startViewTransition(config: LayoutConfig): void {
    const transition = (document as any).startViewTransition(() => {
      this.toggleDarkMode(config);
    });

    transition.ready
      .then(() => {
        this.onTransitionEnd();
      })
      .catch((error: Error) => {
        console.error('View transition error:', error);
      });
  }

  toggleDarkMode(config?: LayoutConfig): void {
    const _config = config || this.layoutConfig();
    if (_config.darkTheme) {
      document.documentElement.classList.add('app-dark');
    } else {
      document.documentElement.classList.remove('app-dark');
    }
  }

  private onTransitionEnd(): void {
    this.transitionComplete.set(true);
    setTimeout(() => {
      this.transitionComplete.set(false);
    });
  }

  onMenuToggle(): void {
    if (this.isOverlay()) {
      this.layoutState.update((prev) => ({
        ...prev,
        overlayMenuActive: !this.layoutState().overlayMenuActive,
      }));

      if (this.layoutState().overlayMenuActive) {
        this.overlayOpen.next();
      }
    }

    if (this.isDesktop()) {
      this.layoutState.update((prev) => ({
        ...prev,
        staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive,
      }));
    } else {
      this.layoutState.update((prev) => ({
        ...prev,
        staticMenuMobileActive: !this.layoutState().staticMenuMobileActive,
      }));

      if (this.layoutState().staticMenuMobileActive) {
        this.overlayOpen.next();
      }
    }
  }

  isDesktop(): boolean {
    return window.innerWidth > 991;
  }

  isMobile(): boolean {
    return !this.isDesktop();
  }

  onConfigUpdate(): void {
    this._config = { ...this.layoutConfig() };
    this.configUpdate.next(this.layoutConfig());
    // Sauvegarder la configuration dans localStorage
    if (isPlatformBrowser(this.platformId)) {
      this.saveConfig();
    }
  }

  private loadConfig(): void {
    try {
      const savedConfig = localStorage.getItem(this.LAYOUT_CONFIG_KEY);
      console.log('Loading saved layout config from localStorage:', savedConfig);

      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        console.log('Parsed config:', config);
        console.log('Default primary color (rose):', this._config.primary);
        console.log('Loaded primary color:', config.primary);

        this.layoutConfig.set({ ...this._config, ...config });
        console.log('Final config applied:', this.layoutConfig());
      } else {
        console.log('No saved config found, using default (rose)');
      }
    } catch (error) {
      console.error('Error loading layout config:', error);
    }
  }

  private saveConfig(): void {
    try {
      localStorage.setItem(this.LAYOUT_CONFIG_KEY, JSON.stringify(this.layoutConfig()));
    } catch (error) {
      console.error('Error saving layout config:', error);
    }
  }

  onMenuStateChange(event: MenuChangeEvent): void {
    this.menuSource.next(event);
  }

  reset(): void {
    this.resetSource.next(true);
  }
}
